"use client";
import React from "react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import { Textarea } from "../ui/textarea";
import { useForm } from "react-hook-form";
import emailjs from "@emailjs/browser";
import { PhoneNumberInput } from "../ui/PhoneNumberInput";

// EmailJS configuration
const publicAPIkey = "SDrjmF9ADy2P9sabf";
const serviceID = "service_fl70gf2";
const templateID = "template_25syksp";

type FormFieldType = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  message?: string;
};

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  message: "",
};

export function SignupForm() {
  const { handleSubmit, reset, register, control, formState: { errors, isSubmitting } } = useForm<FormFieldType>({
    defaultValues: initialValues,
  });

  const onSubmit = async (data: FormFieldType) => {
    const emailParams = {
      firstName: data.firstName,
      lastName: data.lastName || "-",
      email: data.email,
      phoneNumber: data.phoneNumber,
      message: data.message || "-",
    };

    try {
      await emailjs.send(serviceID, templateID, emailParams, publicAPIkey);
      reset(initialValues);
      alert("Message sent successfully!");
    } catch (error) {
      console.error("Failed to send email:", error);
      alert("Failed to send message. Please try again.");
    }
  };
  return (
    <div className="shadow-input mx-auto w-full max-w-md rounded-none p-4 md:rounded-2xl md:py-8 md:px-4 bg-transparent">
      <div className="mb-2 space-y-2 text-center">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-neutral-200">
          Welcome to Nustartz
        </h2>
        <p className="text-sm lg:text-base text-neutral-400">
          We'd love to hear about your vision and challenges. Share your details
          below, and let's explore how we can help your startup scale, innovate,
          and succeed.
        </p>
      </div>

      <form className="my-8" onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4 flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-2">
          <LabelInputContainer>
            <Label htmlFor="firstName" className="text-sm md:text-base">
              First name
              <span className="text-red-400 font-bold ml-2">*</span>
            </Label>
            <Input 
              id="firstName" 
              type="text" 
              className="text-sm md:text-base" 
              {...register("firstName", { required: "First name is required" })}
            />
            {errors.firstName && <span className="text-red-400 text-xs mt-1">{errors.firstName.message}</span>}
          </LabelInputContainer>
          <LabelInputContainer>
            <Label htmlFor="lastName" className="text-sm md:text-base">Last name</Label>
            <Input 
              id="lastName" 
              type="text" 
              className="text-sm md:text-base" 
              {...register("lastName")}
            />
          </LabelInputContainer>
        </div>
        <LabelInputContainer className="mb-4">
          <Label htmlFor="email" className="text-sm md:text-base">
            Email Address
            <span className="text-red-400 font-bold ml-2">*</span>
          </Label>
          <Input 
            id="email" 
            type="email" 
            className="text-sm md:text-base" 
            {...register("email", { 
              required: "Email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address"
              }
            })}
          />
          {errors.email && <span className="text-red-400 text-xs mt-1">{errors.email.message}</span>}
        </LabelInputContainer>
        <div className="mb-4">
          <PhoneNumberInput
            label="Phone Number"
            name="phoneNumber"
            placeholder=""
            control={control}
            isRequired={true}
          />
        </div>
        <LabelInputContainer className="mb-4">
          <Label htmlFor="message" className="text-sm md:text-base">Message</Label>
          <Textarea 
            id="message" 
            rows={5} 
            className="text-sm md:text-base" 
            {...register("message")}
          />
        </LabelInputContainer>

        <button
          className="group/btn relative block h-10 md:h-12 w-full rounded-md bg-gradient-to-br font-medium text-sm md:text-base text-[#466DEC] bg-white from-white to-white shadow-[0px_1px_0px_0px_#27272a_inset,0px_-1px_0px_0px_#27272a_inset] disabled:opacity-50 disabled:cursor-not-allowed"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Sending..." : "Send Message"} &rarr;
          <BottomGradient />
        </button>
      </form>
    </div>
  );
}

const BottomGradient = () => {
  return (
    <>
      <span className="absolute inset-x-0 -bottom-px block h-px w-full bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-0 transition duration-500 group-hover/btn:opacity-100" />
      <span className="absolute inset-x-10 -bottom-px mx-auto block h-px w-1/2 bg-gradient-to-r from-transparent via-indigo-500 to-transparent opacity-0 blur-sm transition duration-500 group-hover/btn:opacity-100" />
    </>
  );
};

const LabelInputContainer = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn("flex w-full flex-col space-y-2", className)}>
      {children}
    </div>
  );
};
